import { useEffect, useMemo, useState } from 'react';
import { GRADE_SUGERIDA, chaveDia, horaDe, ocupacaoDoDia, proximoDiaComVaga } from '@/agenda/grade';
import { TIPOS_AGENDAMENTO } from '@/services/agendamentosService';

/**
 * A grade de avaliação dentro do diálogo de etapa (VF-1).
 *
 * ★ AQUI "LIVRE" É LIVRE, sem ressalva. A agenda de avaliação de marketing é
 * INTERNA deste sistema: toda avaliação de lead é marcada aqui, e quem define
 * quantas vagas existem é a clínica, na aba de Configurações. Diferente da
 * agenda da recepção, que reflete o Careflow e nunca poderia afirmar isso.
 *
 * PROPS-ONLY, como o resto do diálogo: ele também abre no painel da conversa,
 * fora do `LeadsProvider`.
 */

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const rotuloDia = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-').map(Number);
  const data = new Date(a, m - 1, d, 12);
  return `${DIAS[data.getDay()]}, ${d} de ${data.toLocaleDateString('pt-BR', { month: 'long' })}`;
};

const paraIso = (data) => chaveDia(data);

export default function CampoAgendamento({
  grade,
  agendamentosDoDia,
  dia,
  aoTrocarDia,
  hora,
  aoEscolherHora,
  foraDaGrade,
  aoAlternarFora,
  tipo,
  aoTrocarTipo,
  agendamentoFuturo,
  acao,
  aoTrocarAcao,
  carregando = false,
}) {
  const config = grade ?? GRADE_SUGERIDA;
  const data = useMemo(() => {
    if (!dia) return null;
    const [a, m, d] = dia.split('-').map(Number);
    return new Date(a, m - 1, d, 12);
  }, [dia]);

  const ocupacao = useMemo(
    () => (data ? ocupacaoDoDia(config, data, agendamentosDoDia ?? []) : null),
    [config, data, agendamentosDoDia],
  );

  const sugestao = useMemo(() => {
    if (!ocupacao || (!ocupacao.fechado && ocupacao.livres > 0)) return null;
    return proximoDiaComVaga(config, agendamentosDoDia ?? [], data ?? new Date());
  }, [ocupacao, config, agendamentosDoDia, data]);

  return (
    <>
      {/* Já existe consulta marcada: as duas situações são reais e opostas na
          intenção — remarcar (a consulta mudou de dia) e marcar outra (retorno
          de fato). Quem sabe é a pessoa. */}
      {agendamentoFuturo && (
        <div className="aviso">
          <strong>Este lead já tem consulta marcada</strong> para{' '}
          {rotuloDia(paraIso(agendamentoFuturo.dataHora))} às {horaDe(agendamentoFuturo.dataHora)}.
          <div className="linha" style={{ marginTop: 8 }}>
            <label className="filtro-check">
              <input type="radio" checked={acao === 'remarcar'} onChange={() => aoTrocarAcao('remarcar')} />
              Remarcar esta consulta
            </label>
            <label className="filtro-check">
              <input type="radio" checked={acao === 'nova'} onChange={() => aoTrocarAcao('nova')} />
              Marcar outra
            </label>
          </div>
        </div>
      )}

      <div className="linha">
        <div className="campo">
          <label htmlFor="de-dia">Dia da consulta</label>
          <input id="de-dia" type="date" required value={dia} onChange={(e) => aoTrocarDia(e.target.value)} />
        </div>
        {TIPOS_AGENDAMENTO.length > 1 && (
          <div className="campo">
            <label htmlFor="de-tipo">Tipo</label>
            <select id="de-tipo" value={tipo} onChange={(e) => aoTrocarTipo(e.target.value)}>
              {TIPOS_AGENDAMENTO.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {carregando && <p className="campo-ajuda">Carregando a agenda do dia…</p>}

      {ocupacao && !carregando && (
        <section className="secao">
          <h3 className="secao-titulo">{rotuloDia(dia)}</h3>

          {ocupacao.fechado ? (
            <p className="aviso">
              {/* O motivo vem da exceção. Sem ele, um dia bloqueado faz a SDR
                  ligar para o gestor perguntando por quê. */}
              <strong>
                {ocupacao.motivo
                  ? `Fechado — ${ocupacao.motivo}.`
                  : 'A clínica não atende avaliação neste dia.'}
              </strong>
              {sugestao && <> Próximo dia com vaga: <strong>{rotuloDia(paraIso(sugestao))}</strong>.</>}
            </p>
          ) : (
            <>
              <p className="secao-sub">
                {ocupacao.livres} de {ocupacao.vagas} vagas livres.
              </p>
              <div className="filtros-fila">
                {ocupacao.linhas.map((l) => (
                  <button
                    key={l.hora}
                    type="button"
                    className={
                      hora === l.hora ? 'btn-primario' : l.cheio ? 'btn-secundario' : 'btn-secundario'
                    }
                    disabled={l.cheio && hora !== l.hora}
                    title={
                      l.cheio
                        ? `Cheio — ${l.ocupantes.map((o) => o.nome).join(', ')}`
                        : `${l.livres} de ${l.vagas} livres`
                    }
                    onClick={() => aoEscolherHora(l.hora)}
                  >
                    {l.hora}
                    {l.cheio ? ' · cheio' : l.vagas > 1 ? ` · ${l.livres}/${l.vagas}` : ''}
                  </button>
                ))}
              </div>
              {ocupacao.livres === 0 && sugestao && (
                <p className="campo-ajuda">
                  Todos os horários cheios. Próximo dia com vaga:{' '}
                  <strong>{rotuloDia(paraIso(sugestao))}</strong>.
                </p>
              )}
            </>
          )}

          {ocupacao.fora.length > 0 && (
            <p className="campo-ajuda">
              Fora da grade neste dia: {ocupacao.fora.map((a) => `${horaDe(a.dataHora)} ${a.leadNome}`).join(' · ')}
            </p>
          )}
          {ocupacao.retornos.length > 0 && (
            <p className="campo-ajuda">
              Retornos (não ocupam vaga de avaliação):{' '}
              {ocupacao.retornos.map((a) => `${horaDe(a.dataHora)} ${a.leadNome}`).join(' · ')}
            </p>
          )}
        </section>
      )}

      {/* O escape. Sem ele, um caso real que não cabe na grade voltaria a virar
          lead agendado sem consulta — o problema que a peça extingue. Fica
          registrado na timeline para o gestor ver que a grade está apertada. */}
      <label className="filtro-check">
        <input type="checkbox" checked={foraDaGrade} onChange={(e) => aoAlternarFora(e.target.checked)} />
        Marcar fora da grade (encaixe)
      </label>
      {foraDaGrade && (
        <div className="campo">
          <label htmlFor="de-hora-livre">Horário do encaixe</label>
          <input
            id="de-hora-livre"
            type="time"
            required
            value={hora}
            onChange={(e) => aoEscolherHora(e.target.value)}
          />
          <p className="campo-ajuda">
            O encaixe fica registrado na timeline do lead. Se estiver acontecendo com
            frequência, a grade precisa de mais vagas.
          </p>
        </div>
      )}
    </>
  );
}
